import React, { useCallback, useMemo, useRef, useState } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { SignMode, Assign, Div, HumanIcon, AssignGroup, AvatarImage, RightRow1, RightText } from './styles';
import Dropdown from '../../dropdown/mainDropdown';
import { isEmpty } from '../../../helpers/common';
import { AVATAR_BORDER, ICON_SIZE } from '../../../global/constants';
import Avatar from '../../avatar';
import DropdownItem from '../../dropdown/dropdownItem';
import { ItemDiv } from '../../customContextMenu/styles';
import { TaskModel } from '../../../mobx/models/task';
import { UserModel } from '../../../mobx/models/user';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import AssigneeDropdownContent from './assigneeDropdownContent';
import { useMobileDevice } from '../../../global/useMobile';

export type AssignContext =
  | { mode: 'task'; currentTask: TaskModel }
  | { mode: 'recurringTask'; currentTask: TaskModel }
  | { mode: 'taskDetail'; currentTask: TaskModel }
  | { mode: 'createModal' }
  | { mode: 'taskTemplate' }
  | { mode: 'viewFilter' };

interface AssignPopupProps {
  projectUsers: UserModel[];
  context: AssignContext;
  onAssign: (userId: string) => void | Promise<void>;
  onUnassign: () => void | Promise<void>;
  isMultiAssignee?: boolean;
  isContextMenu?: boolean;
  isActive?: boolean;
  setIsDropdownOpen?: (value: boolean) => void;
  updateDropdownOpen?: (value: boolean) => void;
}

const AssignPopup: React.FC<AssignPopupProps> = (props) => {
  const {
    projectUsers,
    context,
    onAssign,
    onUnassign,
    isMultiAssignee = false,
    isContextMenu = false,
    isActive,
    setIsDropdownOpen,
    updateDropdownOpen
  } = props;

  // Derived flags from the discriminated union
  const isFormVariant = context.mode === 'createModal' || context.mode === 'taskTemplate';
  const isDetailVariant = context.mode === 'taskDetail';
  const isViewFilter = context.mode === 'viewFilter';
  const currentTask = 'currentTask' in context ? context.currentTask : undefined;

  // States
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isSearch, setIsSearch] = useState(false);
  const [searchResult, setSearchResult] = useState<UserModel[]>([]);
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const dropdownRef = useRef<{ closeDropdown: () => void }>(null);

  // MobX store state used for trigger UI and "isAssigned" checks
  const { taskStore, projectTemplateStore, viewStore } = rootStore;
  const { createTaskInput, currentAssignBoxTaskId } = taskStore;
  const { currentTaskTemplate } = projectTemplateStore;
  const { createViewInput } = viewStore;

  const isMobile = useMobileDevice();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dataRef = isSearch ? searchResult : projectUsers || [];

  // Match only when the query is a prefix of first OR last name
  const isNamePrefixMatch = (fullName: string, searchText: string) => {
    if (!fullName || !searchText) return false;
    const normalizedSearch = searchText.toLowerCase();
    const words = fullName?.trim()?.toLowerCase()?.split(/\s+/) || [];
    return words.some((word) => word.startsWith(normalizedSearch));
  };

  const searchQuery = useCallback(
    (value: string) => {
      const result = projectUsers?.filter((item) => isNamePrefixMatch(item?.name || '', value)) || [];
      if (isEmpty(value)) setSearchResult(projectUsers || []);
      else setSearchResult(result);
      return null;
    },
    [projectUsers]
  );

  const onChangeSearch = useCallback(
    (value: string) => {
      let timeout;
      setCurrentIndex(0);
      if (!isEmpty(value)) {
        if (!isSearch) setIsSearch(true);
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          searchQuery(value);
        }, 300);
      } else {
        if (isSearch) setIsSearch(false);
        searchQuery(value);
      }
    },
    [isSearch, searchQuery]
  );

  const closeDropdown = useCallback(() => {
    if (setIsDropdownOpen) setIsDropdownOpen(false);
    rootStore.taskStore.clearCurrentAssignBoxTaskId();
    onChangeSearch('');
    setCurrentIndex(0);
    dropdownRef.current?.closeDropdown();
  }, [onChangeSearch, setIsDropdownOpen]);

  const handlePlusIconClick = useCallback(() => {
    setIsDropdownOpen?.(true);
    rootStore.taskStore.setCurrentAssignBoxTaskId(currentTask?.id || '');
  }, [currentTask?.id, setIsDropdownOpen]);

  const isUserAssigned = useCallback(
    (userId: string): boolean => {
      switch (context.mode) {
        case 'createModal':
          return createTaskInput?.users?.some((u: { id: string }) => u.id === userId) ?? false;
        case 'taskTemplate':
          return currentTaskTemplate?.hasAssignee(userId) ?? false;
        case 'viewFilter':
          return createViewInput?.filters?.assign?.assignees?.includes(userId) ?? false;
        case 'task':
        case 'recurringTask':
        case 'taskDetail':
          return context.currentTask?.hasAssignee(userId) ?? false;
        default:
          return false;
      }
    },
    [context, createTaskInput?.users, currentTaskTemplate, createViewInput?.filters?.assign?.assignees]
  );

  const isUnassignedChecked = useMemo(
    () => (isViewFilter ? createViewInput?.filters?.assign?.assignees?.includes('unassigned') ?? false : false),
    [isViewFilter, createViewInput?.filters?.assign?.assignees]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<Element>) => {
      switch (e.key) {
        case 'ArrowUp':
          setCurrentIndex((prevIndex) => (prevIndex === 0 ? dataRef.length - 1 : prevIndex - 1));
          break;
        case 'ArrowDown':
          setCurrentIndex((prevIndex) => (prevIndex === dataRef.length - 1 ? 0 : prevIndex + 1));
          break;
        case 'Enter':
          e.stopPropagation();
          if (dataRef[currentIndex]) onAssign(dataRef[currentIndex].id);
          setCurrentIndex(0);
          if (!isMultiAssignee) closeDropdown();
          break;
        case 'Escape':
          closeDropdown();
          break;
        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentIndex, dataRef, closeDropdown, onAssign, isMultiAssignee]
  );

  const renderFormVariant = () => {
    const users = context.mode === 'taskTemplate' ? currentTaskTemplate?.users : createTaskInput?.users;
    const hasUsers = (users?.length ?? 0) > 0;
    if (hasUsers) {
      return (
        <Assign isCreateTaskModal isActive={isOpenDropdown}>
          {users
            ?.filter((item: UserModel) => !isEmpty(item?.id))
            .map((user: UserModel) => (
              <Div key={user?.id}>
                <Avatar
                  imgSrc={user?.profile_photo || ''}
                  name={user?.name}
                  size={16}
                  borderVariant={AVATAR_BORDER.NONE}
                />
                <p>{user?.name}</p>
              </Div>
            ))}
        </Assign>
      );
    }
    return (
      <HumanIcon isCreateTaskModal isActive={isOpenDropdown}>
        <SVGIcon
          name='customer-icon'
          width={ICON_SIZE.MD}
          height={ICON_SIZE.MD}
          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
        />
        <p>Assignee</p>
      </HumanIcon>
    );
  };

  const renderDetailVariant = () => {
    if (currentTask?.users && currentTask.users.length > 0) {
      return (
        <AssignGroup isActive={isOpenDropdown}>
          <ul>
            {currentTask.users
              ?.filter((item: UserModel) => !isEmpty(item?.id))
              ?.map((item: UserModel) => (
                <li key={item?.id}>
                  <AvatarImage>
                    <Avatar
                      imgSrc={item?.profile_photo || ''}
                      name={item?.name ? item?.name : 'U N'}
                      size={22}
                      borderVariant={AVATAR_BORDER.THIN}
                    />
                  </AvatarImage>
                </li>
              ))}
            <li className='plus-icon'>
              <SVGIcon
                name='plus-icon'
                width={ICON_SIZE.LG}
                height={ICON_SIZE.LG}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill='var(--text-primary)'
              />
            </li>
          </ul>
        </AssignGroup>
      );
    }
    return (
      <RightRow1 isActive={isOpenDropdown}>
        <SVGIcon
          name='unassign-profile-icon'
          width={ICON_SIZE.LG}
          height={ICON_SIZE.LG}
          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
          fill='var(--background-tertiary)'
        />
        <RightText>Unassigned</RightText>
      </RightRow1>
    );
  };

  const renderPlusButton = () => (
    <li
      role='button'
      className={currentAssignBoxTaskId === currentTask?.id ? 'blank-plus-icon' : 'plus-icon'}
      onClick={handlePlusIconClick}>
      <SVGIcon
        name='plus-icon'
        width={ICON_SIZE.LG}
        height={ICON_SIZE.LG}
        viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
        fill='var(--text-primary)'
      />
    </li>
  );

  const renderSignModeContent = useCallback(() => {
    if (isFormVariant) return renderFormVariant();
    if (isDetailVariant) return renderDetailVariant();
    return renderPlusButton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFormVariant,
    isDetailVariant,
    isOpenDropdown,
    createTaskInput?.users,
    currentTaskTemplate?.usersIds,
    currentTaskTemplate?.users,
    currentTask?.id,
    currentTask?.usersIds,
    currentAssignBoxTaskId,
    handlePlusIconClick
  ]);

  const renderData = useCallback(
    (data: UserModel[]) => (
      <AssigneeDropdownContent
        data={data}
        currentIndex={currentIndex}
        isMultiAssignee={isMultiAssignee}
        showUnassignedCheckbox={isViewFilter}
        isUnassignedChecked={isUnassignedChecked}
        isUserAssigned={isUserAssigned}
        onClickUnassignUser={onUnassign}
        onClickAssignUser={(user) => onAssign(user.id)}
      />
    ),
    [currentIndex, isMultiAssignee, isViewFilter, isUnassignedChecked, isUserAssigned, onUnassign, onAssign]
  );

  const renderContent = useMemo(() => {
    return isContextMenu ? (
      <ItemDiv>
        <DropdownItem
          label='Assignee'
          iconName='customer-icon'
          iconSize={16}
          iconViewBox='0 0 18 18'
          isActive={isActive && isContextMenu}
        />
      </ItemDiv>
    ) : (
      <SignMode>{renderSignModeContent()}</SignMode>
    );
  }, [isActive, isContextMenu, renderSignModeContent]);

  return (
    <div onKeyDown={(e) => handleKeyDown(e)}>
      <Dropdown
        ref={dropdownRef}
        isMinWidth={148}
        content={renderContent}
        trigger={isContextMenu && !isMobile ? 'hover' : 'click'}
        modalTitle='Assigns'
        onChangeSearch={onChangeSearch}
        onOutsideClick={() => {
          rootStore.taskStore.clearCurrentAssignBoxTaskId();
        }}
        updateDropdownOpen={(isOpen) => {
          setIsOpenDropdown(isOpen);
          if (!isOpen) rootStore.taskStore.clearCurrentAssignBoxTaskId();
          updateDropdownOpen?.(isOpen);
        }}>
        {isSearch ? renderData(searchResult) : renderData(projectUsers?.filter((user) => !isEmpty(user?.id)) || [])}
      </Dropdown>
    </div>
  );
};

export default observer(AssignPopup);
